// Sound unlock (D-108). Browsers, the iPhone above all, keep a page silent until it is tapped: a voice message or a
// live voice that arrives before the first tap is refused (NotAllowedError) and Web Audio stays suspended. Anything
// that gets refused calls `noteAudioBlocked`, which shows the "Tap to turn on table sound" strip; `unlockAudio`
// (that strip, or any tap or key on the page) resumes every registered AudioContext, plays a silent buffer, runs the
// queued retries and tells `onAudioUnlocked` listeners, all inside the tap so the browser allows it.
import { useSyncExternalStore } from "react";

let blocked = false;
let unlocked = false;
let ctx: AudioContext | null = null;
const contexts = new Set<AudioContext>();
const retries = new Map<string, () => void>();
const unlockListeners = new Set<() => void>();
const listeners = new Set<() => void>();
const emit = () => { for (const l of listeners) l(); };

/** True once a tap has unlocked sound in this tab. */
export const audioUnlocked = (): boolean => unlocked;

/**
 * Something couldn't play because the page hasn't been tapped yet. `retry` (keyed, so a newer one replaces an older
 * one with the same key) runs on the unlocking tap.
 */
export function noteAudioBlocked(key?: string, retry?: () => void): void {
  if (key && retry) retries.set(key, retry);
  if (!blocked) { blocked = true; emit(); }
}

/** A sound engine registers its AudioContext so the unlocking tap resumes it. */
export function registerAudioContext(c: AudioContext): void { contexts.add(c); if (unlocked && c.state === "suspended") void c.resume().catch(() => undefined); }

/** Runs `cb` inside each unlocking tap (prime a media element, resume a context). Returns an unsubscribe. */
export function onAudioUnlocked(cb: () => void): () => void { unlockListeners.add(cb); return () => { unlockListeners.delete(cb); }; }

/** Call from a tap or key handler: turns sound on for this tab and plays whatever was refused. */
export function unlockAudio(): void {
  unlocked = true;
  try {
    const AC = typeof window === "undefined" ? undefined : window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AC) {
      ctx ??= new AC();
      contexts.add(ctx);
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf; src.connect(ctx.destination); src.start(0);
    }
  } catch { /* no Web Audio */ }
  for (const c of contexts) if (c.state === "suspended") void c.resume().catch(() => undefined);
  for (const l of [...unlockListeners]) { try { l(); } catch { /* a listener's own problem */ } }
  const queued = [...retries.values()];
  retries.clear();
  for (const r of queued) { try { r(); } catch { /* retried once */ } }
  if (blocked) { blocked = false; emit(); }
}

/** Whether something is waiting for a tap to play (the strip shows while this is true). */
export function useAudioBlocked(): boolean {
  return useSyncExternalStore((l) => { listeners.add(l); return () => { listeners.delete(l); }; }, () => blocked, () => false);
}

// Any tap or key on the page counts: the strip is only for when nothing has been tapped yet.
if (typeof window !== "undefined") {
  const any = () => { if (!unlocked || blocked) unlockAudio(); };
  window.addEventListener("pointerdown", any, { capture: true, passive: true });
  window.addEventListener("keydown", any, { capture: true });
}

/** Tests. */
export function _resetAudioUnlock(): void { blocked = false; unlocked = false; retries.clear(); contexts.clear(); ctx = null; emit(); }
